import React from "react";
import { useTranslation } from "react-i18next";
import SFButton from "../button";
import getStyled from "../../hooks/getStyled";
import SFTypography from "../typography";
import SFLottieAnimation from "../lottie";
import { SFStack } from "../grid";
import CopilotLoadingAnimation from "../../assets/animations/CopilotLoadingAnimation.json";
import Checkbox from "@mui/material/Checkbox";

const { styled } = getStyled();

const VerticalCard = styled(SFStack, {
  shouldForwardProp: (prop) => prop !== "isActive",
})(({ theme, isActive }) => ({
  width: "310px",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  border: `1px solid ${theme.palette.grey250.main}`,
  borderRadius: "4px",
  padding: theme.spacing(2.5),
  backgroundColor: theme.palette.grey50.main,
  ...(isActive && { border: `1px solid ${theme.palette.blue300.main}` }),
  ...(!isActive && {
    pointerEvents: "none",
  }),
}));

const SFButtonPrimaryStyled = styled(SFButton)(({ theme, disabled }) => ({
  backgroundColor: theme.palette.blue500.main,
  ...(disabled && {
    backgroundColor: `${theme.palette.grey300.main} !important`,
    color: `${theme.palette.white.main} !important`,
  }),
  "&:hover": {
    backgroundColor: theme.palette.white.main,
    color: theme.palette.blue500.main,
    borderColor: theme.palette.blue500.main,
  },
}));

const SFButtonSecondaryStyled = styled(SFButton)(({ theme }) => ({
  border: `1px solid ${theme.palette.blue500.main}`,
  color: theme.palette.blue500.main,
  "&:hover": {
    backgroundColor: theme.palette.blue500.main,
    color: theme.palette.white.main,
  },
}));

const SFStackStyled = styled(SFStack)({
  paddingTop: 8,
  paddingBottom: 8,
});

const ButtonsContainer = styled(SFStack)({
  paddingBottom: 8,
});

const SFLoadingTypography = styled(SFTypography)(({ theme }) => ({
  margin: theme.spacing(1),
  paddingLeft: theme.spacing(12),
  textTransform: "uppercase",
}));

const SFSelectStack = styled(SFStack, {
  shouldForwardProp: (prop) =>
    prop !== "selected" && prop !== "isActive" && prop !== "multipleSelect",
})(({ theme, selected, isActive, multipleSelect }) => ({
  borderRadius: 4,
  marginBottom: theme.spacing(1),
  padding: 0,
  ...(!multipleSelect && {
    padding: theme.spacing(1),
  }),
  ...(isActive
    ? {
      cursor: "pointer",
      backgroundColor: selected
        ? theme.palette.grey450.main
        : theme.palette.white.main,
      border: `1px solid ${
        selected ? theme.palette.grey450.main : theme.palette.grey300.main
      }`,
      "&:hover": {
        backgroundColor: selected
          ? theme.palette.grey450.main
          : theme.palette.grey100.main,
      },
    }
    : {
      backgroundColor: theme.palette.grey100.main,
      border: `1px solid ${
        selected ? theme.palette.grey350.main : theme.palette.grey200.main
      }`,
    }),
}));

const SFStyledTypography = styled(SFTypography, {
  shouldForwardProp: (prop) => prop !== "selected" && prop !== "isActive",
})(({ theme, selected, isActive }) => ({
  color: isActive
    ? selected
      ? theme.palette.white.main
      : theme.palette.grey450.main
    : theme.palette.grey300.main,
}));

const StyledCheckbox = styled(Checkbox)(({ theme, disabled }) => ({
  color: theme.palette.grey450.main,
  "&.Mui-checked": {
    color: disabled ? theme.palette.grey300.main : theme.palette.white.main,
  },
}));

const SelectContainer = styled(SFStack)(({ theme }) => ({
  margin: theme.spacing(1, 0, 2, 0),
}));

const SFCopilotCard = ({
  message,
  isLoading,
  buttonLabelPrimary,
  buttonLabelSecondary,
  isActive,
  buttonActionPrimary = () => {},
  buttonActionSecondary = () => {},
  availableOptions = [],
  multipleSelect = true,
  onSelect = () => {},
}) => {
  const { t } = useTranslation();

  const hasButtons = buttonLabelPrimary || buttonLabelSecondary;

  // Todo: Delete this code when the onSelectHandler is implemented on WEb
  //
  // const onSelectHandler = (item) => {
  //   if (isActive) {
  //     if (multipleSelect) {
  //       setSelectData((prev) => {
  //         return prev.map((prevItem) => {
  //           if (prevItem.label === item.label) {
  //             return {
  //               ...prevItem,
  //               selected: !prevItem.selected,
  //             };
  //           } else {
  //             return prevItem;
  //           }
  //         });
  //       });
  //     } else {
  //       setSelectData((prev) => {
  //         return prev.map((prevItem) => {
  //           if (prevItem.label === item.label) {
  //             return {
  //               ...prevItem,
  //               selected: !prevItem.selected,
  //             };
  //           } else {
  //             return {
  //               ...prevItem,
  //               selected: false,
  //             };
  //           }
  //         });
  //       });
  //     }
  //   }
  // };

  return (
    <VerticalCard isActive={isActive} spacing={2.5}>
      {typeof message === "string" ? (
        <SFTypography variant="body2">{message}</SFTypography>
      ) : (
        message
      )}
      {availableOptions.length > 0 && (
        <SelectContainer>
          {availableOptions.map((option, index) => (
            <SFSelectStack
              key={index}
              selected={option.selected}
              isActive={isActive}
              multipleSelect={multipleSelect}
              direction="row"
              alignItems="center"
              onClick={() => {
                onSelect(option);
              }}
            >
              {multipleSelect && (
                <StyledCheckbox
                  checked={option.selected}
                  disabled={!isActive}
                />
              )}
              <SFStyledTypography
                variant="body2"
                selected={option.selected}
                isActive={isActive}
              >
                {option.label}
              </SFStyledTypography>
            </SFSelectStack>
          ))}
        </SelectContainer>
      )}
      {hasButtons && (
        <ButtonsContainer alignItems="flex-start" spacing={1}>
          {buttonLabelPrimary && (
            <SFButtonPrimaryStyled
              disabled={!isActive}
              variant="primary"
              onClick={buttonActionPrimary}
            >
              {buttonLabelPrimary}
            </SFButtonPrimaryStyled>
          )}
          {buttonLabelSecondary && (
            <SFButtonSecondaryStyled
              disabled={!isActive}
              variant="secondary"
              onClick={buttonActionSecondary}
            >
              {buttonLabelSecondary}
            </SFButtonSecondaryStyled>
          )}
        </ButtonsContainer>
      )}
      {isLoading && (
        <>
          <SFStackStyled alignItems="center" justifyContent="center">
            <SFLottieAnimation
              animationData={CopilotLoadingAnimation}
              loop={true}
              autoplay={true}
            />
          </SFStackStyled>
          <SFLoadingTypography>
            {t("components.copilotCards.cardLoading")}
          </SFLoadingTypography>
        </>
      )}
    </VerticalCard>
  );
};

export default SFCopilotCard;
